// @ts-nocheck — composed component; types added when touched again in a later sub-step
import React from "react";
import KPITile from "../ui/KPITile";

/* composed/KpiStrip.tsx — KPI strip section (Phase 4 sub-step 2).
   Ported from src/legacy/kpi-strip.tsx. The internal data-prep
   (cumulative churn, channel deltas, status emoji) stays identical so
   behavior matches the legacy view byte-for-byte; the only structural
   change is that this file uses the v2 `<KPITile>` primitive (now
   expanded with title/pill/control/sub/valueSub slots) and is exported
   as a real ESM default — no more window.KpiStrip globaling.

   Three exported components:
     <KpiStrip>          wrapper, picks FY vs Period by `mode`
     <KpiStripFY>        FY/YTD branch (4 tiles: NOW/PATH/HEALTH/ECON)
     <KpiStripPeriod>    month/qtr/custom branch (3 tiles)

   InfoIcon and MultiSelectDropdown still come from window (legacy
   primitives.tsx still publishes them). Later sub-steps replace those.
*/

// ─── Internal helpers (formatters that vary with valueMode) ────────────────
function makeFormatters(valueMode) {
  const arrMul = valueMode === "arr" ? 12 : 1;
  const globalEur = (n) => window.__EUR_MRR_PURE((n || 0) * arrMul);
  const mrrFmt = window.__FMT_MONEY_MRR_PURE;
  const lbl = (s) => valueMode === "arr" ? String(s).replace(/MRR/g, "ARR") : s;
  return { arrMul, globalEur, mrrFmt, lbl };
}

// ─── KpiStripFY — 4-tile FY/YTD layout ─────────────────────────────────────
function KpiStripFY({
  lang, valueMode, arpuChannels, includeUpsells, setState,
  rows, lastClosed, todayMonthIdxInRows, currentMrr, expectedNow,
  targetMrr, gap, monthsLeft, avgGrowth, aheadDelta, aheadPct, trackStatus,
  economics, aes,
}) {
  const InfoIcon = window.InfoIcon;
  const MultiSelectDropdown = window.MultiSelectDropdown;
  const { globalEur, lbl } = makeFormatters(valueMode);

  // Cumulative churn for ARR variant of the Churn KPI.
  const cumulativeChurn = rows
    .filter((_, i) => i <= todayMonthIdxInRows)
    .reduce((s, r) => s + (r.churnAbs || 0), 0);
  const curRow = rows[Math.max(0, todayMonthIdxInRows)];
  const curMonthChurn = curRow?.churnAbs || 0;
  const curMonthChurnPct = curRow?.churnPct || 0;
  const cumulativeChurnPct = currentMrr > 0 ? (cumulativeChurn / (currentMrr + cumulativeChurn)) * 100 : 0;
  // Channel performance — last closed vs prior month for MoM trend.
  const lastClosedIdx = lastClosed ? rows.findIndex(r => r.key === lastClosed.key) : -1;
  const priorR = lastClosedIdx > 0 ? rows[lastClosedIdx - 1] : null;
  const ch = (label, cur, prev) => {
    const mom = prev > 0 ? ((cur - prev) / prev) * 100 : (cur > 0 ? 999 : 0);
    return { label, cur: cur || 0, prev: prev || 0, mom };
  };
  // 2026-05 — Marco's directive: Upsell-by-team rolls INTO GTM (it's all
  // "the team's revenue"). The Channel Health card therefore shows GTM as a
  // single combined entry (gtmActual + upsellPipelineActual = gtmTotal)
  // instead of splitting them. Mirrors the forecast table's GTM column.
  // r.gtmTotal is computed in model.ts when both fields are present.
  const gtmCur  = (lastClosed?.gtmActual || 0) + (lastClosed?.upsellPipelineActual || 0);
  const gtmPrev = (priorR?.gtmActual    || 0) + (priorR?.upsellPipelineActual    || 0);
  const channels = lastClosed ? [
    ch("GTM",         gtmCur,                                 gtmPrev),
    ch("Partner",     lastClosed.partnerActual || 0,          priorR?.partnerActual || 0),
    ch("Self-Serve",  lastClosed.selfServeActual || 0,        priorR?.selfServeActual || 0),
    ch("Setup",       lastClosed.setupUpsellActual || 0,      priorR?.setupUpsellActual || 0),
  ].filter(c => c.cur > 0 || c.prev > 0) : [];
  const sortedChannels = [...channels].sort((a, b) => b.cur - a.cur);
  const topCh = sortedChannels[0];
  const lagCh = sortedChannels.length > 1 ? sortedChannels[sortedChannels.length - 1] : null;
  const statusEmoji = trackStatus === "ahead" ? "🟢" : trackStatus === "on" ? "🟢" : (aheadPct < -10 ? "🔴" : "🟠");
  const statusWord = lang === "it"
    ? (trackStatus === "ahead" ? "Avanti" : trackStatus === "on" ? "In linea" : aheadPct < -10 ? "Indietro" : "Attenzione")
    : (trackStatus === "ahead" ? "Ahead" : trackStatus === "on" ? "On track" : aheadPct < -10 ? "Behind" : "Watch out");
  const aeAssigned = (aes || []).filter(a => a.person).length;
  const aeTotal = (aes || []).length;
  return (
  <>
    {/* CARD 1 — NOW: am I in line today? */}
    <KPITile
      accent={`now ${trackStatus || "on-track"}`}
      title={<>{lang === "it" ? "Adesso" : "Now"}{InfoIcon ? <InfoIcon tip={lang === "it"
        ? "Snapshot al momento attuale: MRR raggiunto vs target del giorno (Target oggi/AOT prorata sui giorni del mese), e scostamento dal piano."
        : "Snapshot right now: MRR vs target-of-the-day (Target today/AOT prorated on days in month) and delta vs plan."} /> : null}</>}
      pill={<span className={`status-pill status-pill--${trackStatus}`}><span className="status-dot" />{statusEmoji} {statusWord}</span>}
      value={globalEur(currentMrr)}
      valueLabel={<>{lbl(lang === "it" ? "MRR Attuale" : "Current MRR")} · {lastClosed ? (lang === "it" ? window.MONTH_LABELS_IT[lastClosed.key] : window.MONTH_LABELS_EN[lastClosed.key]) : "—"}</>}
      rows={[
        { label: lang === "it" ? "Target oggi (AOT)" : "Target today (AOT)", value: globalEur(expectedNow) },
        { label: lang === "it" ? "vs piano" : "vs plan",
          value: <>{aheadDelta >= 0 ? "+" : ""}{globalEur(Math.abs(aheadDelta))} ({aheadDelta >= 0 ? "+" : ""}{aheadPct.toFixed(0)}%)</>,
          tone: trackStatus },
      ]}
    />

    {/* CARD 2 — PATH: journey to EOY */}
    <KPITile
      accent="path"
      title={<>{lang === "it" ? "Percorso" : "Path"}{InfoIcon ? <InfoIcon tip={lang === "it"
        ? "Quanto manca a Dicembre 2026: gap residuo e ritmo medio richiesto al mese per chiudere il piano in tempo."
        : "What's left to December 2026: remaining gap and required average monthly pace to hit plan."} /> : null}</>}
      sub={<>{monthsLeft} {lang === "it" ? "mesi rimasti" : "months left"}</>}
      value={globalEur(targetMrr)}
      valueLabel={lang === "it" ? "Target Dicembre 2026" : "December 2026 target"}
      rows={[
        { label: lang === "it" ? "Gap a EOY" : "Gap to EOY", value: globalEur(gap) },
        { label: lang === "it" ? "Ritmo richiesto" : "Required pace",
          value: <>{globalEur(avgGrowth)}/{lang === "it" ? "mese" : "month"}</> },
      ]}
    />

    {/* CARD 3 — HEALTH: which channel is moving, which is lagging. */}
    <KPITile
      accent="health"
      title={<>{lang === "it" ? "Salute Canali" : "Channel Health"}{InfoIcon ? <InfoIcon tip={lang === "it"
        ? "Variazione mese-su-mese (MoM) per canale, basata sull'ultimo mese chiuso. Mostra il canale top + lag, churn % e numero AE assegnati."
        : "Month-over-month variation per channel, based on the last closed month. Shows top + lagging channel, churn %, and assigned AE count."} /> : null}</>}
      sub={<>{lang === "it" ? "MoM" : "MoM"} ({lastClosed ? (lang === "it" ? window.MONTH_LABELS_IT[lastClosed.key] : window.MONTH_LABELS_EN[lastClosed.key]) : "—"})</>}
      value={lastClosed ? globalEur(lastClosed.realAdd || 0) : "—"}
      valueLabel={<>{lbl(lang === "it" ? "Net new MRR" : "Net new MRR")} · {lastClosed ? (lang === "it" ? window.MONTH_LABELS_IT[lastClosed.key] : window.MONTH_LABELS_EN[lastClosed.key]) : "—"}{InfoIcon ? <InfoIcon tip={lang === "it"
        ? `Net new MRR del mese chiuso = somma actuals canali (GTM + Partner + Self-serve + AE + Others reali) meno churn. Mostra quanto è cresciuta la base MRR a netto delle perdite. Formula: realAdd − 0 (la churn è già sottratta nel realEom).`
        : `Net new MRR for the last closed month = sum of channel actuals (GTM + Partner + Self-serve + AE + Others real) minus churn. Shows how much the MRR base grew net of losses. Formula: realAdd − 0 (churn already subtracted in realEom).`} /> : null}</>}
    >
      <div className="kpi-tile__channels">
        {topCh && (
          <div className="kpi-tile__channel kpi-tile__channel--top">
            <span className="kpi-tile__ch-arrow">↑</span>
            <span className="kpi-tile__ch-name">{topCh.label}</span>
            <span className="kpi-tile__ch-mom">{topCh.mom > 999 ? "new" : topCh.mom > 0 ? "+" : ""}{topCh.mom > 999 ? "" : Math.round(topCh.mom) + "%"}</span>
            <span className="kpi-tile__ch-val">{window.__FMT_MONEY_MRR_PURE(topCh.cur)}</span>
          </div>
        )}
        {lagCh && (
          <div className="kpi-tile__channel kpi-tile__channel--lag">
            <span className="kpi-tile__ch-arrow">↓</span>
            <span className="kpi-tile__ch-name">{lagCh.label}</span>
            <span className="kpi-tile__ch-mom">{lagCh.mom > 0 ? "+" : ""}{Math.round(lagCh.mom)}%</span>
            <span className="kpi-tile__ch-val">{window.__FMT_MONEY_MRR_PURE(lagCh.cur)}</span>
          </div>
        )}
        <div className="kpi-tile__channel kpi-tile__channel--churn">
          <span className="kpi-tile__ch-arrow">⊘</span>
          <span className="kpi-tile__ch-name">Churn</span>
          <span className="kpi-tile__ch-mom">{(valueMode === "arr" ? cumulativeChurnPct : curMonthChurnPct).toFixed(1)}%</span>
          <span className="kpi-tile__ch-val">{window.__FMT_MONEY_MRR_PURE(valueMode === "arr" ? cumulativeChurn : curMonthChurn)}</span>
        </div>
        <div className="kpi-tile__channel kpi-tile__channel--ae">
          <span className="kpi-tile__ch-arrow">👤</span>
          <span className="kpi-tile__ch-name">AE</span>
          <span className="kpi-tile__ch-mom">{aeAssigned}/{aeTotal} {lang === "it" ? "assegnati" : "assigned"}</span>
          <span className="kpi-tile__ch-val"></span>
        </div>
      </div>
    </KPITile>

    {/* CARD 4 — ECONOMICS: ARPU + LTV derived from HubSpot deals. */}
    <KPITile
      accent="econ"
      title={<>{lang === "it" ? "Economics" : "Economics"}{InfoIcon ? <InfoIcon tip={lang === "it"
        ? "Unit economics dei clienti: ARPU (ricavo medio per cliente/mese), LTV (valore lifetime per cliente), anzianità media. Calcolati live da HubSpot deals filtrati per canale."
        : "Customer unit economics: ARPU (avg revenue per customer/mo), LTV (lifetime per customer), avg tenure. Computed live from HubSpot deals, channel-filtered."} /> : null}</>}
      control={MultiSelectDropdown ? (
        <MultiSelectDropdown
          value={arpuChannels}
          options={[
            { id: "gtm",       label: "GTM" },
            { id: "selfServe", label: "Self-serve" },
            { id: "partner",   label: "Partner" },
            { id: "other",     label: lang === "it" ? "Other (non-team)" : "Other (non-team)" },
            { id: "upsells",   label: lang === "it" ? "Upsell (+€)" : "Upsells (+€)" },
          ]}
          onChange={(next) => setState(s => ({ ...s, arpuChannels: next }))}
          lang={lang}
          allLabel={lang === "it" ? "Canali" : "Channels"} />
      ) : null}
      sub={<>{economics.activeCount} {lang === "it" ? "clienti" : "customers"}</>}
      value={economics.hasData ? window.__EUR_MRR_PURE(economics.arpu) : "—"}
      valueLabel={<>ARPU · {lang === "it" ? "ricavo medio per cliente / mese" : "avg revenue per customer / mo"}{InfoIcon ? <InfoIcon tip={lang === "it"
        ? `ARPU = ricavi filtrati (€${Math.round(economics.baseRevenue - economics.churnRevenue + economics.upsellRevenue)}) ÷ ${economics.activeCount} clienti attivi.\nCanali: ${arpuChannels.join(", ") || "nessuno"}.\nGli upsell aumentano il numeratore senza contare come nuovi clienti.`
        : `ARPU = filtered revenue (€${Math.round(economics.baseRevenue - economics.churnRevenue + economics.upsellRevenue)}) ÷ ${economics.activeCount} active customers.\nChannels: ${arpuChannels.join(", ") || "none"}.\nUpsells lift revenue without inflating the customer count.`} /> : null}</>}
      valueSub={
        <>
          {includeUpsells && economics.upsellRevenue > 0 && (
            <span className="kpi-tile__value-sub--success" title={lang === "it"
              ? `Upsell aggiunge €${Math.round(economics.upsellRevenue)}/mo da ${economics.upsellCount} contratti — clienti esistenti, non nuovi.`
              : `Upsells add €${Math.round(economics.upsellRevenue)}/mo from ${economics.upsellCount} deals — existing customers, not new.`}>
              ⊕ {window.__EUR_MRR_PURE(economics.upsellRevenue)} {lang === "it" ? "da" : "from"} {economics.upsellCount} {lang === "it" ? "upsell" : "upsells"}
            </span>
          )}
          {includeUpsells && economics.orphanUpsellCount > 0 && (
            <span className="kpi-tile__value-sub--warning" style={{display: "block", marginTop: 4}} title={lang === "it"
              ? `${economics.orphanUpsellCount} upsell(s) sono di clienti il cui contratto base è in un canale NON selezionato (es. Other). Per includerli, attiva il canale corrispondente.`
              : `${economics.orphanUpsellCount} upsell(s) belong to customers whose base deal is in an UNSELECTED channel (e.g. Other). To include them, enable that channel.`}>
              ⚠ {window.__EUR_MRR_PURE(economics.orphanUpsellRevenue)} {lang === "it" ? "in upsell esclusi" : "in excluded upsells"} ({economics.orphanUpsellCount})
            </span>
          )}
        </>
      }
      rows={[
        { label: <>LTV{InfoIcon ? <InfoIcon tip={lang === "it"
            ? `Lifetime Value = ARPU × anzianità media (${economics.avgTenureMonths.toFixed(1)} mesi). Cap a 60m per evitare cifre assurde con campioni piccoli. Invariante MRR/ARR.`
            : `Lifetime Value = ARPU × avg tenure (${economics.avgTenureMonths.toFixed(1)} months). Capped at 60m to avoid absurd figures with small samples. Same in MRR/ARR.`} /> : null}</>,
          value: economics.hasData ? window.__EUR_MRR_PURE(economics.ltv) : "—" },
        { label: lang === "it" ? "Anzianità media" : "Avg tenure",
          value: economics.hasData ? `${economics.avgTenureMonths.toFixed(1)} ${lang === "it" ? "mesi" : "mo"}` : "—" },
      ]}
    />
  </>
  );
}

// ─── KpiStripPeriod — 3-tile month/qtr/custom layout ───────────────────────
function KpiStripPeriod({ lang, valueMode, scope, visibleRows }) {
  const { globalEur, mrrFmt, lbl } = makeFormatters(valueMode);

  const periodTargetEom = scope.endEomTarget;
  const periodRealEom = scope.endEomReal;
  const periodNetTarget = scope.sumNetAddTarget;
  const periodNetReal = scope.sumNetAddReal;
  const periodAheadPct = (periodTargetEom > 0 && periodRealEom != null) ? ((periodRealEom - periodTargetEom) / periodTargetEom) * 100 : null;
  const periodTone = periodAheadPct == null ? "on" : Math.abs(periodAheadPct) < 3 ? "on" : periodAheadPct > 0 ? "ahead" : "behind";
  const mixTotal = scope.sumGtm + scope.sumPartner + scope.sumSelf + scope.sumOthers + scope.sumAeGap;
  const mixPct = (v) => mixTotal > 0 ? (v / mixTotal) * 100 : 0;
  const channels = [
    { label: "GTM",        cur: scope.sumGtm,     color: "var(--lilac-700)" },
    { label: "Partner",    cur: scope.sumPartner, color: "var(--lilac-400)" },
    { label: "Self-Serve", cur: scope.sumSelf,    color: "var(--lilac-300)" },
    { label: "AE",         cur: scope.sumAeGap,   color: "var(--ae-500)" },
    { label: "Others",     cur: scope.sumOthers,  color: "var(--lilac-200)" },
  ].filter(c => c.cur > 0).sort((a, b) => b.cur - a.cur);
  const top = channels[0];
  const lag = channels.length > 1 ? channels[channels.length - 1] : null;
  const statusEmoji = periodTone === "ahead" || periodTone === "on" ? "🟢" : (periodAheadPct != null && periodAheadPct < -10 ? "🔴" : "🟠");
  const statusWord = lang === "it"
    ? (periodTone === "ahead" ? "Avanti" : periodTone === "on" ? "In linea" : (periodAheadPct != null && periodAheadPct < -10 ? "Indietro" : "Attenzione"))
    : (periodTone === "ahead" ? "Ahead" : periodTone === "on" ? "On track" : (periodAheadPct != null && periodAheadPct < -10 ? "Behind" : "Watch"));
  return (
    <>
      {/* CARD 1 — period status: real vs target */}
      <KPITile
        accent={`now ${periodTone}`}
        title={scope.label}
        pill={periodAheadPct != null ? <span className={`status-pill status-pill--${periodTone}`}><span className="status-dot" />{statusEmoji} {statusWord}</span> : null}
        value={periodRealEom != null ? globalEur(periodRealEom) : "—"}
        valueLabel={lbl(lang === "it" ? "Real EOM nel periodo" : "Real EOM in period")}
        rows={[
          { label: lbl(lang === "it" ? "Target EOM" : "EOM target"), value: globalEur(periodTargetEom) },
          { label: lang === "it" ? "vs target" : "vs target",
            value: periodAheadPct == null
              ? (lang === "it" ? "no actuals" : "no actuals")
              : `${periodAheadPct >= 0 ? "+" : ""}${periodAheadPct.toFixed(1)}%`,
            tone: periodTone },
        ]}
      />

      {/* CARD 2 — net add summary */}
      <KPITile
        accent="path"
        title={lbl(lang === "it" ? "Net Add nel periodo" : "Net Add in period")}
        sub={<>{visibleRows.length} {lang === "it" ? "mese/i" : "month(s)"}</>}
        value={globalEur(periodNetTarget)}
        valueLabel={lbl(lang === "it" ? "Target net add MRR" : "Target net add MRR")}
        rows={[
          { label: lbl(lang === "it" ? "Real net add" : "Real net add"),
            value: periodNetReal != null ? globalEur(periodNetReal) : "—" },
          { label: lang === "it" ? "Churn nel periodo" : "Churn in period",
            value: mrrFmt(scope.sumChurn) },
        ]}
      />

      {/* CARD 3 — channel mix in period */}
      <KPITile
        accent="health"
        title={lang === "it" ? "Mix canali (target)" : "Channel mix (target)"}
      >
        <div className="kpi__mix-bar" style={{marginTop: 4}}>
          {channels.map(c => <div key={c.label} className="kpi__mix-seg" style={{flex: c.cur, background: c.color}} title={`${c.label} ${mixPct(c.cur).toFixed(0)}%`} />)}
        </div>
        <div className="kpi-tile__channels">
          {top && (
            <div className="kpi-tile__channel kpi-tile__channel--top">
              <span className="kpi-tile__ch-arrow">↑</span>
              <span className="kpi-tile__ch-name">{top.label}</span>
              <span className="kpi-tile__ch-mom">{mixPct(top.cur).toFixed(0)}%</span>
              <span className="kpi-tile__ch-val">{globalEur(top.cur)}</span>
            </div>
          )}
          {lag && (
            <div className="kpi-tile__channel kpi-tile__channel--lag">
              <span className="kpi-tile__ch-arrow">↓</span>
              <span className="kpi-tile__ch-name">{lag.label}</span>
              <span className="kpi-tile__ch-mom">{mixPct(lag.cur).toFixed(0)}%</span>
              <span className="kpi-tile__ch-val">{globalEur(lag.cur)}</span>
            </div>
          )}
        </div>
      </KPITile>
    </>
  );
}

// ─── KpiStrip — wrapper that switches on `mode` ────────────────────────────
function KpiStrip(props) {
  const { mode } = props;
  if (mode === "fy" || mode === "ytd") return <KpiStripFY {...props} />;
  if (mode === "month" || mode === "qtr" || mode === "custom") return <KpiStripPeriod {...props} />;
  return null;
}

// ─── Memoization (preserves legacy perf wins) ──────────────────────────────
const KpiStripFYMemo = React.memo(KpiStripFY);
const KpiStripPeriodMemo = React.memo(KpiStripPeriod);
const KpiStripMemo = React.memo(KpiStrip);

export default KpiStripMemo;
export { KpiStripFYMemo as KpiStripFY, KpiStripPeriodMemo as KpiStripPeriod };
